import { useMemo, type ReactNode } from "react";
import { StyleSheet, View, type StyleProp, type ViewStyle } from "react-native";
import { type Edge, useSafeAreaInsets } from "react-native-safe-area-context";

import { type Colors, useColors } from "../theme";

interface Props {
  children: ReactNode;
  /**
   * Which safe-area edges to inset. Defaults to the top only — the status
   * bar / notch — which is what every full-bleed screen needs. Pass
   * ["top", "bottom"] for a screen with no pinned footer of its own; the
   * footer/tab-bar chrome owns the bottom inset when present.
   */
  edges?: readonly Edge[];
  /** Extra style merged onto the root (e.g. a one-off background). */
  style?: StyleProp<ViewStyle>;
}

const TOP_ONLY: readonly Edge[] = ["top"];

// The base screen container: a flex-filling, themed-background View that
// owns the safe-area inset so individual screens never re-derive
// `insets.top` by hand. That hand-rolled math is exactly what kept
// stranding content under the notch (the progress bar behind the status
// bar). Purpose-built chrome that needs finer control — appbars, the
// pinned practice footer, the brand split, the network banner — still
// reads insets directly; every plain screen composes this instead.
export function Screen({
  children,
  edges = TOP_ONLY,
  style,
}: Props): JSX.Element {
  const colors = useColors();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const inset: ViewStyle = {
    paddingTop: edges.includes("top") ? insets.top : 0,
    paddingBottom: edges.includes("bottom") ? insets.bottom : 0,
    paddingLeft: edges.includes("left") ? insets.left : 0,
    paddingRight: edges.includes("right") ? insets.right : 0,
  };
  return <View style={[styles.root, inset, style]}>{children}</View>;
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    root: { flex: 1, backgroundColor: colors.background },
  });
}
